/**
 * Password recovery service.
 */

import type { ApiErrorResponse } from '@/types/api';
import { extractErrorMessage } from '@/types/api';
import type {
  PasswordRecoveryAcceptedResponse,
  PasswordRecoveryRequest,
  PasswordRecoveryResetRequest,
  PasswordRecoveryResetResponse,
  PasswordRecoveryValidateResponse,
} from './types';

const API_HEADERS = {
  'Content-Type': 'application/json',
};

export class PasswordRecoveryService {
  static async requestReset(payload: PasswordRecoveryRequest): Promise<PasswordRecoveryAcceptedResponse> {
    const response = await fetch('/api/password-recovery/request', {
      method: 'POST',
      headers: API_HEADERS,
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new PasswordRecoveryError(error, response.status);
    }

    const data = await response.json();
    return data.message;
  }

  static async validateToken(token: string): Promise<PasswordRecoveryValidateResponse> {
    const response = await fetch('/api/password-recovery/validate', {
      method: 'POST',
      headers: API_HEADERS,
      credentials: 'include',
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new PasswordRecoveryError(error, response.status);
    }

    const data = await response.json();
    return data.message;
  }

  static async resetPassword(payload: PasswordRecoveryResetRequest): Promise<PasswordRecoveryResetResponse> {
    const response = await fetch('/api/password-recovery/reset', {
      method: 'POST',
      headers: API_HEADERS,
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new PasswordRecoveryError(error, response.status);
    }

    const data = await response.json();
    return data.message;
  }
}

export class PasswordRecoveryError extends Error {
  constructor(
    public readonly response: ApiErrorResponse,
    public readonly statusCode: number
  ) {
    super(extractErrorMessage(response, 'Password recovery operation failed'));
    this.name = 'PasswordRecoveryError';
  }

  getErrorCode(): string | undefined {
    return this.response.error_code;
  }

  getErrorRef(): string | undefined {
    return this.response.details?.ref || (typeof this.response.detail === 'object' ? this.response.detail?.ref : undefined);
  }
}
